/*
  Dropdown de ordenacion premium — Angela Rozas.
  Capa visual/interactiva sobre el <select name="sort_by"> nativo de
  Shopify, ver snippets/sort-dropdown.liquid para el contrato completo.

  No modifica assets/facets.js ni se registra como listener suyo: se
  limita a actualizar select.value y a disparar un evento "input" real
  sobre el <select>, que es exactamente lo que FacetFiltersForm
  (assets/facets.js) ya escucha de forma nativa sobre el <form> para
  lanzar el AJAX de Shopify. Al ser un custom element, cada vez que
  Shopify reemplaza el HTML de la zona de filtros (AJAX), el navegador
  vuelve a "upgradear" las nuevas instancias de <sort-dropdown>
  automaticamente -- no hace falta re-inicializar nada a mano.
*/

class SortDropdown extends HTMLElement {
  constructor() {
    super();

    this.select = this.querySelector('.ar-sort__native');
    this.trigger = this.querySelector('.ar-sort__trigger');
    this.panel = this.querySelector('.ar-sort__panel');
    this.valueLabel = this.querySelector('.ar-sort__trigger-value');
    this.options = Array.from(this.querySelectorAll('.ar-sort__option'));
    this.closeTimeout = null;

    if (!this.select || !this.trigger || !this.panel) return;

    // Progressive enhancement: el <select> solo se oculta a lectores de
    // pantalla/teclado una vez que el reemplazo (boton + listbox) esta
    // realmente operativo.
    this.select.setAttribute('aria-hidden', 'true');
    this.select.setAttribute('tabindex', '-1');

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerCaptureClick = this.onTriggerCaptureClick.bind(this);
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.onPanelKeydown = this.onPanelKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.panel.addEventListener('keydown', this.onPanelKeydown);
    this.options.forEach((option) => {
      option.addEventListener('click', () => this.selectOption(option));
    });

    // Dentro del drawer mobile, este trigger vive bajo <menu-drawer>
    // (assets/global.js), cuyo bindEvents() añade un listener propio
    // ("cerrar submenu") a TODOS los <button> del drawer sin excluirlo,
    // y ese listener se registra antes que el nuestro (global.js carga
    // en <head>, este archivo carga despues, dentro de la seccion) por
    // lo que se ejecuta primero y cierra el drawer principal. No se
    // puede detener con stopImmediatePropagation() en onTriggerClick
    // (fase bubble, se ejecutaria demasiado tarde). Se intercepta en
    // fase de captura, desde el propio <sort-dropdown> (ancestro del
    // trigger), antes de que el evento llegue al listener de
    // menu-drawer. Solo actua si el click pertenece al trigger -- las
    // opciones (this.panel) y el <select> nativo no son descendientes
    // de this.trigger, asi que no se ven afectados.
    this.addEventListener('click', this.onTriggerCaptureClick, true);
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    window.clearTimeout(this.closeTimeout);
  }

  isOpen() {
    return this.panel && !this.panel.hasAttribute('hidden');
  }

  open() {
    if (this.isOpen()) return;
    window.clearTimeout(this.closeTimeout);
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    const current = this.options.find((option) => option.getAttribute('aria-selected') === 'true');
    window.requestAnimationFrame(() => {
      this.panel.classList.add('ar-sort__panel--open');
      (current || this.options[0])?.focus();
    });
  }

  close(focusTrigger = true) {
    if (!this.isOpen()) return;
    this.panel.classList.remove('ar-sort__panel--open');
    this.trigger.setAttribute('aria-expanded', 'false');
    window.clearTimeout(this.closeTimeout);
    this.closeTimeout = window.setTimeout(() => {
      this.panel.setAttribute('hidden', '');
    }, 220);
    if (focusTrigger) this.trigger.focus();
  }

  onTriggerClick() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onTriggerCaptureClick(event) {
    if (event.target !== this.trigger && !this.trigger.contains(event.target)) return;
    event.stopPropagation();
    this.onTriggerClick();
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  onPanelKeydown(event) {
    const currentIndex = this.options.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        (this.options[currentIndex + 1] || this.options[0])?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        (this.options[currentIndex - 1] || this.options[this.options.length - 1])?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.options[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.options[this.options.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(document.activeElement);
        break;
      case 'Tab':
        this.close(false);
        break;
      default:
        break;
    }
  }

  onDocumentClick(event) {
    if (!this.isOpen()) return;
    if (!this.contains(event.target)) this.close(false);
  }

  selectOption(option) {
    if (!option || !option.classList.contains('ar-sort__option')) return;

    const value = option.dataset.value;
    const label = option.querySelector('.ar-sort__option-label');

    this.options.forEach((item) => {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    });
    if (this.valueLabel && label) {
      const labelText = label.textContent.trim();
      this.valueLabel.textContent = labelText;
      this.trigger.setAttribute('aria-label', `Ordenar por: ${labelText}`);
    }

    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();
  }
}

if (!customElements.get('sort-dropdown')) {
  customElements.define('sort-dropdown', SortDropdown);
}
